import { Component, OnInit, OnDestroy } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import {
  BalanceQuery,
  CartQuery,
  config,
  ErrorStateService,
  MarketplaceItemCart,
  MarketplaceItemStatus,
  MemberDetails,
  MemberQuery,
  MemberService,
  CardTokenQuery,
  MarketplaceService,
  PaymentService,
  ProviderUiActionResponse,
  Address,
  CountryService,
  CountryQuery,
  CartService,
  AddressType,
} from '@fgb/core';
import { UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AddPaymentCardDetailComponent } from '@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail';
import { Router } from '@angular/router';

@Component({
  selector: 'fgb-cart-checkout-page',
  templateUrl: './cart-checkout-page.component.html',
  styleUrls: ['./cart-checkout-page.component.scss'],
})
export class CartCheckoutPageComponent implements OnInit, OnDestroy {
  totalPoints: number;
  totalItems$: Observable<number>;
  items$: Observable<MarketplaceItemCart[] | undefined>;
  editingMemberAddress: boolean = false;
  missingDetails: string = '';
  memberDetail$: Observable<MemberDetails | undefined>;
  memberAddressForm: FormGroup;
  portalId = '';
  hasSubmittedMemberAddress: boolean = false;
  memberDetails: MemberDetails;
  validCheckout: boolean;
  currentBalance: number;
  statusText: string;
  private _destroyed$ = new Subject<void>();
  modalRef: NgbModalRef;
  isSaveCardInfor: boolean = false;
  cardTokenCount: number = 0;
  cardInfor: any = {};
  listItems: any[] = [];
  convertListItems: any[] = [];
  isListHasMerchantItem: boolean = false;
  isShippingEnable: boolean = false;
  totalPriceMerchantValue: number = 0;
  shippingamountValue: number = 0;
  totalBasketPriceValue: number = 0;
  orderId: string = '';
  saveAsCardToken: boolean = false;
  loading: boolean = false;
  config: any = config;
  editingShippingAddress: boolean = false;
  shippingAddressForm: FormGroup;
  hasSubmittedShippingAddress: boolean = false;
  shippingAddress: Address | undefined;
  private shippingAddressBackup: any = null;
  private billingAddressBackup: any = null;

  constructor(
    private location: Location,
    private cartService: CartService,
    private cartQuery: CartQuery,
    private errorService: ErrorStateService,
    private memberQuery: MemberQuery,
    private formBuilder: UntypedFormBuilder,
    private memberService: MemberService,
    private balanceQuery: BalanceQuery,
    private translate: TranslateService,
    private modalService: NgbModal,
    private cardTokenQuery: CardTokenQuery,
    private marketplaceService: MarketplaceService,
    private paymentService: PaymentService,
    private router: Router,
    private countryService: CountryService,
    private countryQuery: CountryQuery
  ) {}

  ngOnInit() {
    this.errorService.clearErrors();
    this.countryService.fetchCountries().toPromise();

    this.cartQuery
      .updateBasketPointsTotal()
      .pipe(
        tap((val) => {
          this.totalPoints = val ? val : 0;
        })
      )
      .toPromise();

    this.cardTokenQuery
      .selectUnfrozenCards()
      .pipe(
        tap((tokens) => {
          if (tokens.length > 0) {
            this.cardTokenCount = tokens.length;
            this.cardInfor = { ...tokens[0] };
          }
        })
      )
      .toPromise();

    this.balanceQuery
      .selectPurse(config.purseConfig.virtualLoyalty)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((bal) => {
        this.currentBalance = bal ?? 0;
      });
    this.items$ = this.cartQuery.selectAllMarketplaceCartData().pipe(
      tap((items) => {
        this.validCheckout = this._validateItems(items);
        if (items) {
          const specificMerchantItem = items.find((item: MarketplaceItemCart) => item.productData.MerchantValue);
          const shippingItem = items.find((item: MarketplaceItemCart) => item.productData.ShippingEnabled);
          if (specificMerchantItem && specificMerchantItem.productData.MerchantValue) {
            this.isListHasMerchantItem = true;
          }
          if (shippingItem && shippingItem.productData.ShippingEnabled) {
            this.isShippingEnable = true;
          }
          this.listItems = items;

          const orderBody = items.map((item: MarketplaceItemCart) => {
            return {
              Id: item.productData.Id,
              RewardItemId: item.productData.ProductItemId,
              GameNumber: item.productData.GameNumber,
              AttributeMappingId: item.attributes.AttributeMappingId,
              Quantity: item.purchaseQuantity,
              CustomFields: item.customFields,
            };
          });
          this.convertListItems = orderBody;

          this.marketplaceService.checkoutStart(orderBody).then((res) => {
            if (res?.OrderId) {
              this.shippingAddress = res.Basket.ShippingAddress;
              this.orderId = res.OrderId;
              if (this.memberDetails) {
                this.syncShippingAndTotals();
              }
            }
          });
        }
      })
    );
    this.totalItems$ = this.cartQuery.updateTotalItems();

    // get address from member details
    this.getMemberDetailAddress();
  }

  private async syncShippingAndTotals(updateShippingAddress: boolean = true, updateAddress: boolean = true, addressForm = {}) {
    if (!this.orderId) return;

    const payload = {
      ...this.memberDetails,
      ...addressForm,
      CountryId: (this.memberDetails as any).CountryId ?? null,
      CountyId: (this.memberDetails as any).CountyId ?? null,
    };

    if (updateAddress) {
      await this.marketplaceService.updateAdress(this.orderId, payload).then((shippingValues) => {
        this.totalPriceMerchantValue = shippingValues.TotalMerchant;
        this.shippingamountValue = shippingValues.ShippingMerchant;
        this.totalBasketPriceValue = shippingValues.BasketTotalMerchant as number;
      });
    }

    if (updateShippingAddress) {
      await this.marketplaceService.updateShippingAdress(this.orderId, payload).then((shippingValues) => {
        this.totalPriceMerchantValue = shippingValues.TotalMerchant;
        this.shippingamountValue = shippingValues.ShippingMerchant;
        this.totalBasketPriceValue = shippingValues.BasketTotalMerchant as number;
      });
    }
  }

  // go back to previous page from checkout page
  goBack() {
    this.location.back();
  }

  /**
   * Pay with eixsting card token
   */
  async payNow() {
    this.loading = true;
    const res = await this.marketplaceService.completePurchaseBasketOrder(
      this.orderId,
      '/checkout',
      '/rewards/marketplace/purchase/success',
      !!this.cardInfor.CardNumber,
      false
    );

    const orderId = res?.Data?.OrderId || '';
    if (orderId) {
      this.router.navigate([`rewards/marketplace/purchase/success`], { queryParams: { purchaseOrderId: orderId } });
    }
  }

  /**
   * Checkout with new card
   */
  async checkout(saveAsCardToken: boolean = false) {
    this.marketplaceService
      .completePurchaseBasketOrder(
        this.orderId,
        this.config.portalUrl + '/ecash/failure',
        this.config.portalUrl + '/checkout/results/',
        false,
        saveAsCardToken
      )
      .then((response) => {
        this.paymentService.process(
          response.Data as ProviderUiActionResponse,
          'payment-gateway-area',
          config.portalUrl + '/portalproducts/failure'
        );
      });
  }

  getMemberDetailAddress() {
    this.memberDetail$ = this.memberQuery.selectMemberDetails().pipe(
      tap((md) => {
        if (md) {
          this.memberDetails = md;
          this.portalId = md.PortalId;
          this.memberAddressForm = this.formBuilding(this.memberAddressForm, { ...md, AddressType: AddressType.BillingAddress1 });
          this.shippingAddressForm = this.formBuilding(this.shippingAddressForm, {
            ...md,
            AddressType: AddressType.ShippingAddress1,
          });

          this.memberAddressForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
            if (this.memberAddressForm.valid && this.orderId) {
              this.memberDetails = {
                ...this.memberDetails,
                ...this.memberAddressForm.value,
              };
            }
          });

          this.shippingAddressForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
            if (this.shippingAddressForm.valid && this.orderId) {
              this.memberDetails = {
                ...this.memberDetails,
                ...this.shippingAddressForm.value,
              };
            }
          });
        }
      })
    );
  }

  /** Start editing the user's billing details address. */
  editMemberAddress() {
    this.editingMemberAddress = true;
  }

  /** Start editing the user's shipping details address. */
  editShippingAddress() {
    this.editingShippingAddress = true;
  }

  /** Clear the address form fields but keep a backup */
  clearAddressForm(addressForm: FormGroup) {
    if (!this.billingAddressBackup) {
      this.billingAddressBackup = { ...addressForm.getRawValue() };
    }

    this.clearForm(addressForm);
  }

  clearShippingAddressForm(addressForm: FormGroup) {
    if (!this.shippingAddressBackup) {
      this.shippingAddressBackup = { ...addressForm.getRawValue() };
    }

    this.clearForm(addressForm);
  }

  /** Clear the address form fields. */
  clearForm(addressForm: FormGroup) {
    addressForm.patchValue({
      Street: '',
      Town: '',
      County: '',
      PostCode: '',
      Country: null,
      HomeNumber: '',
      CountryId: null,
      CountyId: null,
    });
  }

  /** cancel button which will close and rebuild the form with the original data */
  cancelAndRebuildForm() {
    this.editingMemberAddress = false;

    if (this.billingAddressBackup) {
      this.memberAddressForm.patchValue(this.billingAddressBackup);
      this.billingAddressBackup = null;
      return;
    }

    this.memberDetail$ = this.memberQuery.selectMemberDetails().pipe(
      tap((md) => {
        if (md) {
          this.portalId = md.PortalId;
          this.formPatching(this.memberAddressForm, md);
        }
      })
    );
  }

  /** cancel button which will close and rebuild the form with the original data */
  cancelAndRebuildShippingForm() {
    this.editingShippingAddress = false;

    if (this.shippingAddressBackup) {
      this.shippingAddressForm.patchValue(this.shippingAddressBackup);
      this.shippingAddressBackup = null;
      return;
    }

    if (this.memberDetails) {
      const md = this.memberDetails;
      this.formPatching(this.shippingAddressForm, md);
    }
  }

  formPatching(addressForm: FormGroup, md: MemberDetails) {
    return addressForm.patchValue({
      AddressName: md.AddressName,
      Street: addressForm?.get('Street')?.value ?? md.Street,
      Town: addressForm?.get('Town')?.value ?? md.Town,
      County: addressForm?.get('County')?.value ?? md.County,
      Country: addressForm?.get('Country')?.value ?? md.Country,
      PostCode: addressForm?.get('PostCode')?.value ?? md.PostCode,
      HomeNumber: addressForm?.get('HomeNumber')?.value ?? md.HomeNumber,
      CountryId: md.CountryId ?? null,
      CountyId: md.CountyId ?? null,
      AddressType: addressForm?.get('AddressType')?.value,
    });
  }

  formBuilding(addressForm: FormGroup, md: MemberDetails) {
    return this.formBuilder.group({
      AddressName: [md.AddressName],
      Street: [addressForm?.get('Street')?.value ?? md.Street, Validators.required],
      Town: [addressForm?.get('Town')?.value ?? md.Town, Validators.required],
      County: [addressForm?.get('County')?.value ?? md.County, Validators.required],
      Country: [addressForm?.get('Country')?.value ?? md.Country, Validators.required],
      PostCode: [addressForm?.get('PostCode')?.value ?? md.PostCode, Validators.required],
      HomeNumber: [addressForm?.get('HomeNumber')?.value ?? md.HomeNumber, Validators.required],
      CountryId: [md.CountryId ?? null],
      CountyId: [md.CountyId ?? null],
      AddressType: [md.AddressType],
    });
  }

  retrieveCountries(form: FormGroup) {
    const countryOptions = this.countryQuery.getAll();
    const CountrySelected = countryOptions.filter((c) => c.CountryName === form?.get('Country')?.value);
    form?.get('CountryId')?.patchValue(CountrySelected[0]?.ID ?? 0);
    const regions = CountrySelected[0]?.Regions;
    if (regions) {
      const CountySelected = regions.filter((c) => c?.RegionName === form?.get('County')?.value);
      form?.get('CountyId')?.patchValue(CountySelected[0]?.RegionId ?? 0);
    }
  }

  /** SAVE BILLING ADDRESS */
  saveMemberDetailAddress() {
    this.retrieveCountries(this.memberAddressForm);
    this.hasSubmittedMemberAddress = true;
    this.saveAddress(this.memberAddressForm, 'billing');
  }

  /** SAVE SHIPPING ADDRESS */
  saveShippingAddress() {
    this.retrieveCountries(this.shippingAddressForm);
    this.hasSubmittedShippingAddress = true;
    this.saveAddress(this.shippingAddressForm, 'shipping');
  }

  saveAddress(addressForm: FormGroup, addressType: string) {
    if (addressForm.valid) {
      const tempAddressForm = addressForm.value;

      this.memberDetails = {
        ...this.memberDetails,
        ...addressForm.value,
        CountryId: tempAddressForm.CountryId ?? this.memberDetails?.CountryId ?? null,
        CountyId: tempAddressForm.CountyId ?? this.memberDetails?.CountyId ?? null,
      };

      if (addressType == 'shipping') {
        this.syncShippingAndTotals(true, false, addressForm.value);
        this.shippingAddressBackup = null;
        this.editingShippingAddress = false;
      } else if (addressType == 'billing') {
        this.memberService.updateMemberDetails(this.portalId, this.memberDetails);
        this.syncShippingAndTotals(false, true, addressForm.value);
        this.billingAddressBackup = null;
        this.editingMemberAddress = false;
      }

      this.errorService.clearErrors();
    }
  }

  // open modal add cart payment detail
  openCartAddPaymentDetailModal(saveAsCardToken: boolean = false) {
    const formValue = this.memberAddressForm;
    this.modalRef = this.modalService.open(AddPaymentCardDetailComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
    });
    this.modalRef.componentInstance.formValue = formValue;
    this.modalRef.componentInstance.checkoutMode = true;
    this.modalRef.componentInstance.closeModal = this.closeModal.bind(this);
    this.modalRef.componentInstance.redirectUrl = '/checkout/results/';
    this.modalRef.componentInstance['closeModalEvent'].subscribe(() => {
      this.closeModal();
    });
    this.modalRef.componentInstance['submittedAddressEvent'].subscribe(() => {
      this.checkout(saveAsCardToken); //< you now have access to the event that was emitted, to pass to your grandfather component.
    });
  }

  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  submittedAddress() {
    this.payNow();
  }

  saveCardInfor(isSave: boolean) {
    this.isSaveCardInfor = isSave;
  }

  ngOnDestroy(): void {
    this.cartService.removeAllCartItems();
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private _validateItems(items: MarketplaceItemCart[] | undefined) {
    if (!items || items.length === 0) {
      this.translate
        .stream('checkout.empty-basket')
        .pipe(takeUntil(this._destroyed$))
        .subscribe((data: string) => {
          this.statusText = data;
        });
      return false;
    }

    for (let item of items) {
      switch (item.productData.Status) {
        case MarketplaceItemStatus.SoldOut:
          this.translate
            .stream('marketplace-details.sold-out')
            .pipe(takeUntil(this._destroyed$))
            .subscribe((data: string) => {
              this.statusText = data;
            });
          return false;
        case MarketplaceItemStatus.NotEnoughPoints:
          this.translate
            .stream('marketplace-details.not-enough')
            .pipe(takeUntil(this._destroyed$))
            .subscribe((data: string) => {
              this.statusText = data;
            });
          return false;
        case MarketplaceItemStatus.ComingSoon:
          this.translate
            .stream('marketplace-details.coming-soon')
            .pipe(takeUntil(this._destroyed$))
            .subscribe((data: string) => {
              this.statusText = data;
            });
          return false;
        case MarketplaceItemStatus.MemberLimitReached:
          this.translate
            .stream('marketplace-details.limit-reached')
            .pipe(takeUntil(this._destroyed$))
            .subscribe((data: string) => {
              this.statusText = data;
            });
          return false;
      }
    }

    this.translate
      .stream('checkout-claim-now')
      .pipe(takeUntil(this._destroyed$))
      .subscribe((data: string) => {
        this.statusText = data;
      });
    return true;
  }
}
